import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactMarkdown from "react-markdown";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { BlogPost, getPostBySlug, formatDate } from "../utils/blogUtils";

// Default fallback image from Unsplash
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";

const BlogPostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) {
        setError("Post not found");
        setLoading(false);
        return;
      }

      try {
        const postData = await getPostBySlug(slug);
        if (postData) {
          setPost(postData);
        } else {
          setError("Post not found");
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Error loading post");
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
    // Scroll to the top when the component mounts
    window.scrollTo(0, 0);
  }, [slug]);

  const handleImageError = () => {
    console.error(`Failed to load cover image: ${post?.coverImage}`);
    setImageError(true);
  };

  // Function to test image validity
  useEffect(() => {
    if (post?.coverImage) {
      const img = new Image();
      img.src = post.coverImage;
      img.onerror = () => setImageError(true);
    }
  }, [post]);

  if (loading) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Navbar />
        <Container sx={{ py: 8, minHeight: "60vh" }}>
          <Typography variant="h4" align="center" gutterBottom>
            {error || "Post not found"}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              component={RouterLink}
              to="/blog"
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                background: "linear-gradient(45deg, #2196F3, #21CBF3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #1976D2, #2196F3)",
                },
              }}
            >
              Back to Blog
            </Button>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  const coverImage = imageError
    ? DEFAULT_IMAGE
    : post.coverImage || DEFAULT_IMAGE;

  return (
    <>
      <Navbar />
      <Box
        sx={{
          backgroundColor: "#f9fafb",
          minHeight: "100vh",
        }}
      >
        {/* Hero Image */}
        <Box
          sx={{
            height: { xs: 240, sm: 320, md: 480 },
            position: "relative",
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6)), url(${coverImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <Container maxWidth="md" sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
            <Chip
              label={formatDate(post.date)}
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                color: "primary.main",
                fontWeight: 600,
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: "white",
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" },
                lineHeight: 1.2,
                textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              {post.title}
            </Typography>
          </Container>
        </Box>

        {/* Article Content */}
        <Container maxWidth="md" sx={{ mt: -4 }}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              backgroundColor: "white",
              mb: 6,
            }}
          >
            {/* Author & Post Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 4,
                pb: 3,
                borderBottom: "1px solid",
                borderColor: "rgba(0, 0, 0, 0.08)",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 48,
                  height: 48,
                }}
              >
                {post.author.charAt(0)}
              </Avatar>
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {post.author}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {post.author === "Renoten Team"
                    ? "Our team of productivity experts"
                    : "Productivity Specialist"}
                </Typography>
              </Box>
            </Box>

            {/* Markdown Content */}
            <Box
              className="markdown-content"
              sx={{
                typography: "body1",
                color: "#2a2a2a",
                fontSize: { xs: "1rem", md: "1.1rem" },
                lineHeight: 1.7,
                "& h1": {
                  mt: 5,
                  mb: 3,
                  typography: "h3",
                  fontWeight: 700,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                  color: "#1a1a1a",
                },
                "& h2": {
                  mt: 4,
                  mb: 2.5,
                  typography: "h4",
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "1.8rem" },
                  color: "#1a1a1a",
                },
                "& h3": {
                  mt: 3.5,
                  mb: 2,
                  typography: "h5",
                  fontWeight: 700,
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  color: "#1a1a1a",
                },
                "& p": { mb: 2.5 },
                "& ul, & ol": { mb: 2.5, pl: 2.5 },
                "& li": { mb: 1.5, pl: 0.5 },
                "& a": {
                  color: "primary.main",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 2,
                  my: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
                "& blockquote": {
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                  pl: 3,
                  py: 1,
                  my: 3,
                  fontStyle: "italic",
                  bgcolor: "rgba(33, 150, 243, 0.05)",
                  borderRadius: "0 4px 4px 0",
                },
                "& code": {
                  fontFamily: "monospace",
                  bgcolor: "rgba(0, 0, 0, 0.05)",
                  p: 0.5,
                  borderRadius: 0.5,
                  fontSize: "90%",
                },
                "& pre": {
                  bgcolor: "rgba(0, 0, 0, 0.05)",
                  p: 3,
                  borderRadius: 2,
                  overflow: "auto",
                  my: 3,
                  "& code": {
                    bgcolor: "transparent",
                    p: 0,
                    display: "block",
                    lineHeight: 1.6,
                  },
                },
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  mb: 3,
                  mt: 3,
                  "& th, & td": {
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    p: 1.5,
                  },
                  "& th": {
                    bgcolor: "rgba(0, 0, 0, 0.05)",
                    fontWeight: 700,
                  },
                },
              }}
            >
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </Box>

            {/* Back to Blog Button */}
            <Divider sx={{ my: 5 }} />

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                component={RouterLink}
                to="/blog"
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Back to all articles
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default BlogPostDetail;
